import type { ConditionAlertDefinition } from '@thinkalpha/platform-ws-client/contracts/alerts.js';

export type ConditionOperation = 'greaterThan' | 'lessThan';

/**
 * The condition type from ConditionAlertDefinition, but explicitly nullable.
 * This is needed because the initial state has no condition until synced from a ConditionAlertModel or definition.
 */
export type AlertCondition = ConditionAlertDefinition['condition'] | null;

/**
 * State shape for an alert row, managed by the reducer.
 * Excludes subscription-derived state (indicatorValue, priceDirection, connectionStatus)
 * which are managed separately as signals outside the reducer.
 */
export interface AlertRowState {
    /** The condition for triggering the alert (null before initialization) */
    condition: AlertCondition;
    /** Whether the alert is enabled */
    enabled: boolean;
    /** Whether to show a toast notification when triggered */
    showToast: boolean;
    /** Whether to play a sound when triggered */
    playSound: boolean;
    /** The type of sound to play */
    soundType: string;
    /** Whether to speak an alert message via TTS when triggered */
    speakTts: boolean;
    /** Custom override text for TTS (undefined = auto-generated from alert context) */
    ttsText: string | undefined;
    /** The voiceURI to use for TTS (undefined = system default) */
    ttsVoiceUri: string | undefined;
    /** TTS speech rate (0.1-10, default 1) */
    ttsRate: number;
    /** TTS pitch (0-2, default 1) */
    ttsPitch: number;
    /** TTS volume (0-1, default 1) */
    ttsVolume: number;
    /** Optional note for the alert */
    note: string | null;
    /**
     * Local operation state - allows direction to be set before a value is entered.
     * When the condition has no value (min and max are null), this tracks the user's
     * intended direction for when they do enter a value.
     */
    localOperation: ConditionOperation;
}

/**
 * Create the initial state for a new alert row.
 */
export function createInitialAlertRowState(): AlertRowState {
    return {
        condition: null,
        enabled: false,
        showToast: true,
        playSound: true,
        soundType: 'tambourine',
        speakTts: false,
        ttsText: undefined,
        ttsVoiceUri: undefined,
        ttsRate: 1,
        ttsPitch: 1,
        ttsVolume: 1,
        note: null,
        localOperation: 'greaterThan',
    };
}
